import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "@/components/app-shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DeveloperMCPPanel } from "@/components/settings/DeveloperMCPPanel";
import { MessageSquare, CreditCard, Bot, Code, Database, Activity } from "lucide-react";

export const Route = createFileRoute("/settings/integrations-hub")({
  head: () => ({ meta: [{ title: "Integrations & API Keys · AVS ERP" }] }),
  component: IntegrationsHubPage,
});

function IntegrationsHubPage() {
  return (
    <div className="space-y-4" data-testid="integrations-hub">
      <PageHeader
        title="Integrations & API Keys"
        description="WhatsApp, SMS, email, payments, AI provider, MCP, and storage — one place."
      />
      <Tabs defaultValue="communications">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="communications">Communications</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="ai">AI Provider</TabsTrigger>
          <TabsTrigger value="developer">Developer / MCP</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
        </TabsList>
        <TabsContent value="communications" className="space-y-3 mt-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MessageSquare className="h-4 w-4 text-gold" /> WhatsApp, SMS & Email
            </div>
            <p className="text-xs text-muted-foreground">
              Configure customer-owned API keys for outbound messaging. AVS does not resell SMS/WhatsApp credits.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/settings/communications">Open communications settings</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/settings/whatsapp">WhatsApp automation</Link>
              </Button>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CreditCard className="h-4 w-4 text-gold" /> Tenant payments
            </div>
            <p className="text-xs text-muted-foreground">
              Customer invoice payments via Razorpay (when enabled for your firm).
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/license">License & billing</Link>
            </Button>
          </Card>
        </TabsContent>
        <TabsContent value="ai" className="mt-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Bot className="h-4 w-4 text-gold" /> Clearmove AI provider
            </div>
            <p className="text-xs text-muted-foreground">
              Firm-scoped AI API keys for Clearmove AI Center (MCP tool layer).
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/ai-center">Open AI Center</Link>
            </Button>
          </Card>
        </TabsContent>
        <TabsContent value="developer" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-3">
              <Code className="h-4 w-4 text-gold" /> MCP & API tokens
            </div>
            <DeveloperMCPPanel />
          </Card>
        </TabsContent>
        <TabsContent value="storage" className="mt-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Database className="h-4 w-4 text-gold" /> Cloud storage (R2)
            </div>
            <p className="text-xs text-muted-foreground">
              Media storage is platform-managed. Run diagnostics to verify connectivity.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/storage-diagnostics">
                <Activity className="h-3.5 w-3.5 mr-1" /> Storage diagnostics
              </Link>
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
